<admin-app-header></admin-app-header>

<div class="container mt-5 mb-5">
    <div class="row">
        <div class="col-lg-7">
            <h1><i class="fas fa-flask me-2"></i>PayGate Lab</h1>
            <p class="text-muted mb-2">
                End-to-end test of the PayWeb3 redirect migration: load a card on PayGate's hosted page, hold an
                amount against the resulting token, then settle or void it.
            </p>
            <div class="alert alert-danger py-2">
                <i class="fas fa-triangle-exclamation me-1"></i>
                <strong>This is live PayGate.</strong> Real money moves. Amounts are capped at R{{ maxAmountRands }}.
                Nothing here touches subscriptions or invoices.
            </div>
            <hr />

            <!-- Step 1: client -->
            <div class="mb-4">
                <label class="form-label fw-bold">1. Select client</label>
                <input
                    class="form-control"
                    placeholder="Name, surname, company, email or client ID..."
                    [typeahead]="clientOptions"
                    [typeaheadOptionField]="'search'"
                    [typeaheadItemTemplate]="clientItemTemplate"
                    [typeaheadSingleWords]="false"
                    [typeaheadMinLength]="2"
                    [typeaheadOptionsLimit]="15"
                    (typeaheadOnSelect)="onClientSelect($event)"
                    [(ngModel)]="clientSearchText"
                />
                <ng-template #clientItemTemplate let-model="item">
                    <div>
                        <strong>{{ model.name }}</strong>
                        <small class="text-muted d-block">{{ model.detail }}</small>
                    </div>
                </ng-template>
                <div *ngIf="selectedClient" class="mt-2 alert alert-info mb-0 py-2">
                    <strong>{{ selectedClient.firstName }} {{ selectedClient.lastName }}</strong>
                    <span class="text-muted ms-2">{{ selectedClient.$key }}</span>
                </div>
            </div>

            <!-- Step 2: tokenise via PayGate hosted page -->
            <div class="mb-4" *ngIf="selectedClient">
                <label class="form-label fw-bold">2. Load a card (PayWeb3 redirect)</label>
                <p class="text-muted small mb-2">
                    PayGate hosts the card form — no card data touches our servers. The card is vaulted and appears
                    in the list below.
                </p>
                <div class="input-group mb-2" style="max-width: 320px">
                    <span class="input-group-text">R</span>
                    <input type="number" class="form-control" [(ngModel)]="tokeniseAmountRands" step="0.01" min="0.01" [max]="maxAmountRands" />
                    <button class="btn btn-primary" (click)="startTokenise()" [disabled]="tokenising || !isTokeniseAmountValid">
                        <span *ngIf="tokenising" class="spinner-border spinner-border-sm me-1"></span>
                        Add card via PayGate
                    </button>
                </div>
                <div class="form-text mb-2">
                    Note: this amount is charged, not held — the 3DS account settles immediately and we do not get it back.
                </div>

                <!-- Hidden form posts into the iframe below (PayWeb3 step 3). -->
                <form #payWeb3Form [action]="payWeb3.processUrl" method="POST" target="paygateLabFrame" style="display: none">
                    <input type="hidden" name="PAY_REQUEST_ID" [value]="payWeb3.payRequestId" />
                    <input type="hidden" name="CHECKSUM" [value]="payWeb3.checksum" />
                </form>

                <!-- One frame, always mounted: destroying it mid-flight would lose PayGate's redirect. -->
                <div class="border rounded p-2 mb-2" [hidden]="!showFrame">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <small class="text-muted">PayGate hosted page</small>
                        <button class="btn btn-sm btn-outline-secondary" (click)="cancelFrame()">Close</button>
                    </div>
                    <iframe name="paygateLabFrame" style="width: 100%; height: 520px; border: 0"></iframe>
                </div>

                <div *ngIf="labSession?.status === 'complete'" class="alert py-2 mb-0" [ngClass]="labSession?.vaultId ? 'alert-success' : 'alert-warning'">
                    <div *ngIf="labSession?.vaultId">
                        Vaulted <strong>{{ labSession.maskedCard }}</strong> — token <code>{{ labSession.vaultId }}</code>
                    </div>
                    <div *ngIf="!labSession?.vaultId">
                        No token returned. TRANSACTION_STATUS {{ labSession.transactionStatus }}
                        <span *ngIf="labSession.resultDesc">— {{ labSession.resultDesc }}</span>
                    </div>
                </div>
            </div>

            <!-- Recent capture attempts, so a lost return leg can be recovered -->
            <div class="mb-4" *ngIf="recentSessions.length">
                <label class="form-label fw-bold">Recent card captures</label>
                <div *ngFor="let session of recentSessions" class="d-flex align-items-center border rounded p-2 mb-1">
                    <div class="small">
                        <span
                            class="badge me-2"
                            [ngClass]="session.status === 'complete' ? (session.vaultId ? 'bg-success' : 'bg-warning') : 'bg-secondary'"
                        >{{ session.status === 'complete' ? (session.vaultId ? 'vaulted' : 'no token') : session.status }}</span>
                        <span *ngIf="session.maskedCard">{{ session.maskedCard }}</span>
                        <span *ngIf="!session.maskedCard" class="text-muted">R{{ session.amountInCents / 100 }} — {{ session.payRequestId || 'no PAY_REQUEST_ID' }}</span>
                    </div>
                    <button
                        class="btn btn-sm ms-auto"
                        [ngClass]="session.vaultId ? 'btn-outline-secondary' : 'btn-outline-primary'"
                        *ngIf="session.payRequestId"
                        (click)="completeSession(session.documentId)"
                        [disabled]="completingSession === session.documentId"
                    >
                        <span *ngIf="completingSession === session.documentId" class="spinner-border spinner-border-sm me-1"></span>
                        {{ session.vaultId ? 'Re-query (show raw fields)' : 'Retrieve from PayGate' }}
                    </button>
                </div>
                <div class="form-text">
                    If PayGate took the payment but the browser never made it back, the card is still vaulted on their
                    side — retrieve it here rather than paying again. Re-querying a completed capture is free and
                    read-only; use it to see exactly which fields PayGate returns.
                </div>
            </div>

            <!-- Step 3: card selection -->
            <div class="mb-4" *ngIf="selectedClient">
                <label class="form-label fw-bold">3. Select a vaulted card</label>
                <div *ngIf="!paymentMethodsLoaded" class="text-muted">Loading cards...</div>
                <div *ngIf="paymentMethodsLoaded && paymentMethods.length === 0" class="alert alert-warning py-2">
                    No active cards for this client — load one above.
                </div>
                <div
                    *ngFor="let pm of paymentMethods"
                    class="card mb-2 p-2"
                    [class.border-primary]="selectedPaymentMethod?.$key === pm.$key"
                    (click)="selectCard(pm)"
                    style="cursor: pointer"
                >
                    <div class="d-flex align-items-center">
                        <input type="radio" class="form-check-input me-3" [checked]="selectedPaymentMethod?.$key === pm.$key" name="cardSelect" />
                        <div>
                            <strong>{{ pm.cardNo }}</strong>
                            <span class="text-muted ms-2">{{ pm.nameOnCard }}</span>
                            <span *ngIf="pm.primary" class="badge bg-success ms-2">Primary</span>
                        </div>
                        <div class="ms-auto text-end">
                            <small class="text-muted d-block">Token: {{ pm.vaultId ? 'yes' : 'no' }}</small>

                        </div>
                    </div>
                </div>
            </div>

            <!-- Step 4: authorise -->
            <div class="mb-4" *ngIf="selectedPaymentMethod">
                <label class="form-label fw-bold">4. Authorise a hold on the token</label>
                <div class="alert alert-secondary py-2 small mb-2">
                    <strong>The key test.</strong> After the migration we never see a CVV again. Turn the toggle off
                    and confirm PayGate still authorises — if it does, we can stop storing CVVs.
                </div>
                <div class="alert alert-light border py-2 small mb-2">
                    CVVs are no longer stored, so every request goes without one. The
                    <code>Recurring</code> block below is what makes that work.
                </div>
                <div class="border rounded p-2 mb-2 bg-light">
                    <div class="fw-bold small mb-1">Stored-credential options</div>
                    <div class="form-text mb-2">
                        These are in PayGate's live WSDL but not in their PDF. They are the two documented ways to
                        charge a stored card without a CVV — try them with "Send stored CVV" switched off.
                    </div>

                    <div class="form-check form-switch mb-2">
                        <input class="form-check-input" type="checkbox" id="cvvToggle" [(ngModel)]="cvvToggle" />
                        <label class="form-check-label" for="cvvToggle">
                            Send <code>&lt;CvvToggle&gt;true&lt;/CvvToggle&gt;</code>
                        </label>
                    </div>

                    <div class="row g-2">
                        <div class="col-sm-6">
                            <label class="form-label small mb-0">Recurring block</label>
                            <select class="form-select form-select-sm" [(ngModel)]="recurringType">
                                <option value="">Not sent</option>
                                <option value="RECURRING">RECURRING</option>
                                <option value="CARD_ON_FILE">CARD_ON_FILE</option>
                            </select>
                        </div>
                        <div class="col-sm-6" *ngIf="recurringType">
                            <label class="form-label small mb-0">Initiated by</label>
                            <select class="form-select form-select-sm" [(ngModel)]="initiatedType">
                                <option value="MERCHANT">MERCHANT (unattended)</option>
                                <option value="CARD_HOLDER">CARD_HOLDER</option>
                            </select>
                        </div>
                    </div>

                    <div class="form-check mt-2" *ngIf="recurringType">
                        <input class="form-check-input" type="checkbox" id="initialAuth" [(ngModel)]="initialAuth" />
                        <label class="form-check-label small" for="initialAuth">
                            <code>InitialAuth</code> — this is the first transaction in the series
                        </label>
                    </div>

                    <div class="mt-2" *ngIf="recurringType">
                        <label class="form-label small mb-0">
                            <code>OriginalRecurringReferenceID</code> <span class="text-muted">(optional — the first transaction's ID)</span>
                        </label>
                        <input class="form-control form-control-sm" [(ngModel)]="originalRecurringReferenceId" placeholder="e.g. 1243700418" />
                    </div>
                </div>

                <div class="input-group mb-2" style="max-width: 320px">
                    <span class="input-group-text">R</span>
                    <input type="number" class="form-control" [(ngModel)]="holdAmountRands" step="0.01" min="0.01" [max]="maxAmountRands" />
                    <button class="btn btn-primary" (click)="authorise()" [disabled]="authorising || !isHoldAmountValid">
                        <span *ngIf="authorising" class="spinner-border spinner-border-sm me-1"></span>
                        Authorise
                    </button>
                </div>

                <button class="btn btn-outline-secondary btn-sm" (click)="lookupVault()" [disabled]="lookingUp">
                    <span *ngIf="lookingUp" class="spinner-border spinner-border-sm me-1"></span>
                    <i class="fas fa-magnifying-glass me-1"></i> Which account owns this token?
                </button>
                <div class="form-text">
                    Diagnostic for result code 900019 "Card vault out of scope". Runs LookupVault against both the
                    MOTO and PGS accounts. Moves no money.
                </div>
            </div>

            <!-- Step 5: settle / void -->
            <div class="mb-4" *ngIf="transactionId">
                <label class="form-label fw-bold">5. Settle or void the hold</label>
                <p class="text-muted small mb-2">Transaction <code>{{ transactionId }}</code></p>
                <button class="btn btn-success me-2" (click)="settle()" [disabled]="settling">
                    <span *ngIf="settling" class="spinner-border spinner-border-sm me-1"></span>
                    Settle (capture)
                </button>
                <button class="btn btn-outline-danger" (click)="voidHold()" [disabled]="voiding">
                    <span *ngIf="voiding" class="spinner-border spinner-border-sm me-1"></span>
                    Void (release)
                </button>
            </div>
        </div>

        <!-- Result log -->
        <div class="col-lg-5">
            <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Result log</h5>
                <button class="btn btn-sm btn-outline-secondary" (click)="clearLog()" *ngIf="log.length">Clear</button>
            </div>
            <hr />
            <p class="text-muted" *ngIf="!log.length">Nothing yet — run a step on the left.</p>

            <div *ngFor="let entry of log; let i = index" class="card mb-2">
                <div class="card-body py-2 px-3">
                    <div class="d-flex align-items-center">
                        <i class="fas me-2" [ngClass]="entry.ok ? 'fa-circle-check text-success' : 'fa-circle-xmark text-danger'"></i>
                        <strong>{{ entry.step }}</strong>
                        <small class="text-muted ms-auto">{{ entry.at | date: 'HH:mm:ss' }}</small>
                    </div>
                    <div class="small mt-1">{{ entry.message }}</div>
                    <button class="btn btn-link btn-sm px-0" (click)="toggleRaw(i)" *ngIf="entry.raw">
                        {{ showRawFor[i] ? 'Hide' : 'Show' }} raw PayGate response
                    </button>
                    <pre *ngIf="showRawFor[i]" class="small bg-light p-2 mb-0" style="max-height: 320px; overflow: auto">{{ asJson(entry.raw) }}</pre>
                </div>
            </div>
        </div>
    </div>
</div>
